import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/feature/Header";
import Footer from "../../components/feature/Footer";
import ScrollToTop from "../../components/feature/ScrollToTop";
import SEOHead from "../../components/base/SEOHead";
import CategoryFilter from "./components/CategoryFilter";
import MatchCard from "./components/MatchCard";
import DateTabs from "./components/DateTabs";
import { todayMatches, tomorrowMatches, yesterdayMatches } from "../../mocks/sportsSchedule";
import type { MatchData } from "../../mocks/sportsSchedule";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://xn--9z2b23f95curk71m6qd.com";

const scheduleJsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "오늘의 스포츠중계 일정",
  description: "실시간 스포츠중계 일정과 경기 결과를 한눈에 확인하세요.",
  numberOfItems: todayMatches.length,
  itemListElement: todayMatches.map((match, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "SportsEvent",
      name: `${match.homeTeam} vs ${match.awayTeam}`,
      sport: match.category,
      startDate: new Date().toISOString().split("T")[0],
      homeTeam: { "@type": "SportsTeam", name: match.homeTeam },
      awayTeam: { "@type": "SportsTeam", name: match.awayTeam },
    },
  })),
};

export default function SportsSchedule() {
  const [activeTab, setActiveTab] = useState("today");
  const [activeCategory, setActiveCategory] = useState("all");

  const matchesByTab: Record<string, MatchData[]> = {
    yesterday: yesterdayMatches,
    today: todayMatches,
    tomorrow: tomorrowMatches,
  };

  const currentMatches = matchesByTab[activeTab] || [];

  const filteredMatches = useMemo(() => {
    if (activeCategory === "all") return currentMatches;
    return currentMatches.filter((match) => match.category === activeCategory);
  }, [currentMatches, activeCategory]);

  const liveCount = filteredMatches.filter((m) => m.status === "live").length;
  const upcomingCount = filteredMatches.filter((m) => m.status === "upcoming").length;
  const finishedCount = filteredMatches.filter((m) => m.status === "finished").length;

  const liveMatches = filteredMatches.filter((m) => m.status === "live");
  const otherMatches = filteredMatches.filter((m) => m.status !== "live");

  const sortedMatches = [...liveMatches, ...otherMatches];

  return (
    <div className="min-h-screen bg-dark-bg">
      <SEOHead
        title="오늘의 스포츠중계 | 실시간 경기 일정 및 결과 - 할매티비"
        description="오늘의 스포츠중계 실시간 일정을 확인하세요. 해외축구, MLB, NBA, K리그 등 모든 경기의 생중계 일정과 결과를 한눈에 제공합니다. 할매티비에서 지금 바로 확인해보세요."
        keywords="오늘의스포츠중계,스포츠중계일정,실시간경기일정,해외축구일정,MLB일정,NBA일정,생중계일정"
        canonicalPath="/sports-schedule"
        jsonLd={scheduleJsonLd}
      />
      <Header />
      <main className="pt-24 md:pt-32 pb-16">
        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
          {/* Page Title */}
          <div className="mb-8">
            <h1 className="text-white font-bold text-2xl md:text-3xl mb-2">
              오늘의 스포츠중계
            </h1>
            <p className="text-dark-text-muted text-base">
              실시간 스포츠중계 일정과 경기 결과를 한눈에 확인하세요.
            </p>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-3 gap-3 mb-8">
            <div className="bg-dark-surface border border-dark-border rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                <span className="text-dark-text-muted text-xs">생중계</span>
              </div>
              <span className="text-white font-bold text-xl">{liveCount}</span>
            </div>
            <div className="bg-dark-surface border border-dark-border rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="w-2 h-2 rounded-full bg-brand-orange"></span>
                <span className="text-dark-text-muted text-xs">예정</span>
              </div>
              <span className="text-white font-bold text-xl">{upcomingCount}</span>
            </div>
            <div className="bg-dark-surface border border-dark-border rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="w-2 h-2 rounded-full bg-dark-text-muted"></span>
                <span className="text-dark-text-muted text-xs">종료</span>
              </div>
              <span className="text-white font-bold text-xl">{finishedCount}</span>
            </div>
          </div>

          {/* Date Tabs */}
          <DateTabs activeTab={activeTab} onTabChange={setActiveTab} />

          {/* Category Filter */}
          <CategoryFilter activeCategory={activeCategory} onCategoryChange={setActiveCategory} />

          {/* Live Matches Section */}
          {liveMatches.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                <h2 className="text-red-500 font-bold text-lg">실시간 생중계</h2>
                <span className="text-dark-text-muted text-sm">({liveMatches.length}경기)</span>
              </div>
              <div className="space-y-3">
                {liveMatches.map((match) => (
                  <MatchCard key={match.id} match={match} />
                ))}
              </div>
            </div>
          )}

          {/* Other Matches */}
          {otherMatches.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-white font-bold text-lg">
                  {activeTab === "today" ? "오늘의 경기" : activeTab === "tomorrow" ? "내일의 경기" : "어제의 경기"}
                </h2>
                <span className="text-dark-text-muted text-sm">({otherMatches.length}경기)</span>
              </div>
              <div className="space-y-3">
                {otherMatches.map((match) => (
                  <MatchCard key={match.id} match={match} />
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {sortedMatches.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-dark-surface rounded-full border border-dark-border">
                <i className="ri-calendar-line text-dark-text-muted text-3xl"></i>
              </div>
              <p className="text-dark-text-muted text-base">
                선택한 조건에 맞는 경기가 없습니다.
              </p>
              <button
                onClick={() => {
                  setActiveCategory("all");
                  setActiveTab("today");
                }}
                className="mt-4 text-brand-orange hover:text-brand-orange-hover font-medium text-sm transition-colors cursor-pointer"
              >
                전체 경기 보기
              </button>
            </div>
          )}

          {/* CTA */}
          <div className="text-center mt-12 pt-8 border-t border-dark-border">
            <p className="text-dark-text-muted text-sm mb-4">
              모든 경기를 고화질로 실시간 시청하세요.
            </p>
            <Link
              to="/live/content/4ksportslive"
              className="group inline-flex items-center gap-3 bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-base py-4 px-10 rounded-full transition-all whitespace-nowrap"
            >
              <i className="ri-live-line text-xl"></i>
              <span>스포츠중계 바로보기</span>
              <i className="ri-arrow-right-up-line text-lg opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"></i>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}